import { parse } from 'cookie';
import { differenceInYears } from 'date-fns';
import { isArray, isPlainObject } from 'lodash';
import {
  type Coverage,
  type CoverageProduct,
  type MarketableProduct,
  MarketableProductName,
} from '../dto/marketableProduct';
import { Language } from '../i18n';
import { type EditableFormValues } from '../individual/components/BackgroundInfo/EditableForm';
import type { InitialState } from '../individual/types/initialState';
import { type SelectedSumInsuredPremiums } from '../individual/types/selectedSumInsuredPremiums';
import { type Feature } from '../types/featureSwitches';
import {
  type NamedCompanyOrCommunityBeneficiary,
  type NamedPersonBeneficiary,
} from 'dto/beneficiary';
import { type CoveragePremium } from 'dto/CoveragePremium';
import { type ProductDocument } from 'dto/productDocument';
import { type BackgroundInfoForm } from 'individual/types/backgroundInfoForm';

export { formatEuros, formatCentsToEuros } from './currency-utils';

export function getValidAgeRange(marketableProduct: MarketableProduct) {
  let minValidAge: number = Number.POSITIVE_INFINITY;
  let maxValidAge: number = Number.NEGATIVE_INFINITY;
  for (const coverage of marketableProduct.coverages) {
    if (coverage.minGrantingAge < minValidAge) {
      minValidAge = coverage.minGrantingAge;
    }
    if (coverage.maxGrantingAge > maxValidAge) {
      maxValidAge = coverage.maxGrantingAge;
    }
  }
  return {
    min: minValidAge,
    max: maxValidAge,
  };
}

export function redirectTo(url: string, params?: any) {
  const queryParamString = params
    ? `?${new URLSearchParams(params).toString()}`
    : '';
  window.location.assign(url + queryParamString);
}

export function getProductParam(): string {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('product') ?? MarketableProductName.INDIVIDUAL_LIFE;
}

// This function takes a date object as a parameter and returns the date as a string in ISO8601 format (no timezone)
// This way the timezone does not matter as it takes the timezone offset into account. For example:
// Tue Oct 31 1989 16:00:00 GMT-0800 (Pacific Standard Time) returns 1989-11-01
// Wed Nov 01 1989 02:00:00 GMT+0200 (Eastern European Standard Time) returns 1989-11-01
export function formLocalDateUTC(date: Date): string {
  return date.toISOString().split('T')[0];
}

// This function always needs to get the date with hours. For example Tue Oct 31 1989 16:00:00 GMT-0800 (Pacific Standard Time)
// has the hours at 16. When creating a js Date object from a date such as 1989-11-01 the hours are always there and are formed according
// to the users timezone. However the Opux DateInput returns the date with 0 hours. For example 1.11.1989 is Wed Nov 1 1989 00:00:00 GMT-0800 (Pacific Standard Time)
// But this is actually the past date when expressed in UTC time (31.10.1989). This is why onSubmit function in EditableForm.tsx forms an ISO date of this object
// before converting it to a js Date object (that has the necessary hours)
export function toBackgroundInfoForm(
  editableFormValues: EditableFormValues
): BackgroundInfoForm {
  const dateOfBirth = editableFormValues.dateOfBirth as Date;
  const dateOfBirthLocalDate = formLocalDateUTC(dateOfBirth);
  return {
    ...editableFormValues,
    monthlyIncome: editableFormValues.monthlyIncome
      ? Number(editableFormValues.monthlyIncome)
      : undefined,
    debt: editableFormValues.debt ? Number(editableFormValues.debt) : undefined,
    dateOfBirth: dateOfBirthLocalDate,
  };
}

// Formats date into d.M.yyyy format in UTC time
export const formatDate = (date: Date) => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // add 1 to month to get a 1-based index
  const day = date.getUTCDate();
  return `${day}.${month}.${year}`;
};

export function getInitialState(): InitialState {
  return window.__RHVKLSO002_INITIAL_STATE;
}

export function getPath(path: string): string {
  return `/${path}`;
}

export const getSuggestedPremium = (
  selectedCoverage: CoveragePremium[],
  coverageProduct: CoverageProduct,
  deductibleDays?: number | null
) => {
  return selectedCoverage
    .find(
      (coveragePremium) => coveragePremium.coverageProduct === coverageProduct
    )
    ?.premiums.find((premium) => {
      const isDeductibleDaysMatch = deductibleDays
        ? premium.deductibleDays === deductibleDays
        : true;
      return premium.suggestedSumInsured.isSuggested && isDeductibleDaysMatch;
    });
};

export const getPremiumBySumInsured = (
  selectedCoverage: CoveragePremium[],
  coverageProduct: CoverageProduct,
  sumInsured: number,
  deductibleDays?: number | null
) => {
  return selectedCoverage
    .find(
      (coveragePremium) => coveragePremium.coverageProduct === coverageProduct
    )
    ?.premiums.find((premium) => {
      const isDeductibleDaysMatch = deductibleDays
        ? premium.deductibleDays === deductibleDays
        : true;
      return premium.sumInsured === sumInsured && isDeductibleDaysMatch;
    });
};

export const getNamedBeneficiaryIdentifier = (
  beneficiary: NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary
) => {
  return 'ssn' in beneficiary
    ? beneficiary.ssn
    : 'businessId' in beneficiary
      ? beneficiary.businessId
      : undefined;
};

export const sortProductDocuments = (documents: ProductDocument[]) => {
  if (!Array.isArray(documents)) {
    return [];
  }
  return documents.sort((a, b) => a.documentName.localeCompare(b.documentName));
};

/**
 * Check if the the app is running in a webview.
 * @returns {boolean}
 */
export const isContentOnly = (): boolean => {
  return (
    document.cookie
      .split('; ')
      .find((row) => row.startsWith('op_theme_state='))
      ?.split('=')[1] === 'contentOnlyMobile'
  );
};

export enum PremiumPeriod {
  MONTHLY = 'monthlyNetPremium',
  ANNUAL = 'annualNetPremium',
  MONTHLY_GROSS = 'monthlyGrossPremium',
  ANNUAL_GROSS = 'annualGrossPremium',
}

export const getTotal = (
  selectedSumInsuredPremiums: SelectedSumInsuredPremiums,
  selectedCoverages: CoverageProduct[],
  premiumPeriod: PremiumPeriod
) => {
  return selectedCoverages
    .map(
      (coverageProduct) =>
        selectedSumInsuredPremiums[coverageProduct]?.[premiumPeriod]
          ?.amountInCents ?? 0
    )
    .reduce((sum, current) => sum + current, 0);
};

export const getMonthlyTotal = (
  selectedSumInsuredPremiums: SelectedSumInsuredPremiums,
  selectedCoverages: CoverageProduct[]
) =>
  getTotal(
    selectedSumInsuredPremiums,
    selectedCoverages,
    PremiumPeriod.MONTHLY
  );

export const getAnnualTotal = (
  selectedSumInsuredPremiums: SelectedSumInsuredPremiums,
  selectedCoverages: CoverageProduct[]
) =>
  getTotal(selectedSumInsuredPremiums, selectedCoverages, PremiumPeriod.ANNUAL);

export const getMonthlyGross = (
  selectedSumInsuredPremiums: SelectedSumInsuredPremiums,
  selectedCoverages: CoverageProduct[]
) =>
  getTotal(
    selectedSumInsuredPremiums,
    selectedCoverages,
    PremiumPeriod.MONTHLY_GROSS
  );

export const getAnnualGross = (
  selectedSumInsuredPremiums: SelectedSumInsuredPremiums,
  selectedCoverages: CoverageProduct[]
) =>
  getTotal(
    selectedSumInsuredPremiums,
    selectedCoverages,
    PremiumPeriod.ANNUAL_GROSS
  );

export const birthdateToAge = (birthdate: string): number => {
  return differenceInYears(
    new Date(getInitialState().currentDate).setHours(0, 0, 0, 0),
    new Date(birthdate).setHours(0, 0, 0, 0)
  );
};

export const withinAgeRange = (coverage: Coverage, dateOfBirth: string) => {
  const age = birthdateToAge(dateOfBirth);
  return age
    ? age >= coverage.minGrantingAge && age <= coverage.maxGrantingAge
    : true;
};

/**
 * Checks if the user has consented to operational development cookie.
 * @returns {boolean}
 */
export const isOperationalDevelopmentCookieAccepted = () => {
  try {
    const parsedCookie = parse(document.cookie);
    if (!isPlainObject(parsedCookie)) {
      return false;
    }
    const consentCookieValue =
      parsedCookie['op.authConsent'] || parsedCookie['op.anoConsent'];

    if (!consentCookieValue) {
      return false;
    }
    const consentCookie = JSON.parse(consentCookieValue);
    if (!consentCookie || !isArray(consentCookie.categories)) {
      return false;
    }

    return consentCookie.categories[1] === 1;
  } catch (e) {
    console.log('Failed to read cookie consent value:');
    console.log(e);
    return false;
  }
};

export function isFeatureEnabled(feature: Feature): boolean {
  return getInitialState()?.featureSwitches?.[feature] ?? false;
}

export const formatNumber = (number: number) => {
  return new Intl.NumberFormat(Language.fi).format(number);
};

export const getSmallestMaxGrantingAge = (
  coverages: Coverage[]
): number | undefined => {
  if (!coverages || coverages.length === 0) {
    return undefined;
  }
  return coverages.reduce(
    (min, coverage) => Math.min(min, coverage.maxGrantingAge),
    Number.POSITIVE_INFINITY
  );
};

export const getCoverageByCoverageProduct = (
  coverages: Coverage[],
  coverageProduct: CoverageProduct
) => {
  return coverages.find(
    (coverage) => coverage.coverageProduct === coverageProduct
  );
};
